// Depth maps: a distance for every pixel of a render (Corona CZDepth, saved as EXR), turned into a
// mesh of the full-detail scene around that render. Walking then projects the renders onto the real
// shape of the furniture instead of the low-poly proxy.
//
// Depth data here is metres from the camera, row 0 = the bottom of the image (texture order, matching
// eq() in the shaders), same width/height ratio as the render (2:1).
import * as THREE from 'three';
import { fuseDepth } from './fusion';

/** Equirectangular pixel (u, v in 0..1, v = 0 at the bottom) to a world direction, matching eq() in shaders.ts. */
export function eqDir(u: number, v: number, yaw: number, flip: boolean, out = new THREE.Vector3()): THREE.Vector3 {
  if (flip) u = 1 - u;
  const lon = (u - 0.5) * 2 * Math.PI + yaw;
  const lat = (v - 0.5) * Math.PI;
  return out.set(Math.cos(lat) * Math.sin(lon), Math.sin(lat), -Math.cos(lat) * Math.cos(lon));
}

/** Far enough to count as sky or open view. */
export const DEPTH_FAR = 150;

export interface DepthArrays {
  pos: Float32Array; idx: Uint32Array;
  /** what the clean-up took out, as loose triangles (for showing it), and how many of each kind */
  removed?: Float32Array; stray?: number; loose?: number;
}

/**
 * Builds the mesh of what a render saw: a sphere around the camera, pushed out to the depth of each pixel.
 * Triangles that bridge a jump in depth (the edge of an island bench against the wall behind) are left out,
 * so nothing smears across the gap; another render fills it in.
 *
 * Self-contained on purpose (no imports, nothing from outside the function): the engine runs it in a
 * worker from its own source text, so walking doesn't stall while a mesh is built.
 */
export function depthArrays(depth: Float32Array, w: number, h: number, cx: number, cy: number, cz: number, yaw: number, flip: boolean, gridW: number, minFacing: number, nearLayer = true,
  others: Array<[Float32Array, number, number, number, number, number, number]> | null = null, clean = 0, minIsland = 0): DepthArrays {
  const FAR = 150;
  const gw = Math.max(16, Math.round(gridW)), gh = Math.max(8, Math.round(gw / 2));
  const nv = (gw + 1) * (gh + 1);
  const pos = new Float32Array(nv * 3);
  const dist = new Float32Array(nv);
  for (let j = 0; j <= gh; j++) {
    const v = j / gh;
    const py = Math.min(h - 1, Math.max(0, Math.floor(v * h)));
    const lat = (v - 0.5) * Math.PI, cl = Math.cos(lat), sl = Math.sin(lat);
    for (let i = 0; i <= gw; i++) {
      let u = i / gw;
      const px = Math.min(w - 1, Math.floor((i % gw) / gw * w));
      // the nearest of the few pixels under this vertex: keeps thin things (table legs) rather than the wall behind
      const px2 = Math.min(w - 1, px + 1), py2 = Math.min(h - 1, py + 1);
      let d = Math.min(depth[py * w + px], depth[py * w + px2], depth[py2 * w + px], depth[py2 * w + px2]);
      if (!(d > 0.02)) d = 0.02;
      if (d > FAR) d = FAR;
      if (flip) u = 1 - u;
      const lon = (u - 0.5) * 2 * Math.PI + yaw;
      const k = j * (gw + 1) + i;
      dist[k] = d;
      pos[k * 3] = cx + cl * Math.sin(lon) * d; pos[k * 3 + 1] = cy + sl * d; pos[k * 3 + 2] = cz - cl * Math.cos(lon) * d;
    }
  }
  // Which edges cross a jump in depth (the bench's edge against the floor behind). The change in depth
  // along an edge is compared with the edges either side of it in the same direction: a grazing wall or
  // floor changes smoothly, so it stays whole; a jump stands out from its neighbours.
  const at = (i: number, j: number) => j * (gw + 1) + (((i % gw) + gw) % gw);
  const lr = (i0: number, j0: number, i1: number, j1: number) => {
    if (j0 < 0 || j1 < 0 || j0 > gh || j1 > gh) return 0;
    return Math.abs(Math.log(dist[at(i1, j1)] / dist[at(i0, j0)]));
  };
  const jump = (i: number, j: number, di: number, dj: number) => {
    const l = lr(i, j, i + di, j + dj);
    if (l < 0.04) return false;
    if (l > 0.7) return true; // twice as far or more
    // the smaller side: an anti-aliased edge splits a jump over two steps, and each mustn't hide the other
    const side = Math.min(lr(i - di, j - dj, i, j), lr(i + di, j + dj, i + 2 * di, j + 2 * dj));
    return l > 3.5 * side + 0.03;
  };
  // Surfaces seen almost edge-on (a wall running away from the camera) are left out with nothing in their
  // place: this render only sees them as a thin sliver, and another render that faces them does better.
  const edgeOn = (a: number, b: number, c: number) => {
    if (Math.min(dist[a], dist[b], dist[c]) >= FAR) return true;
    const ax = pos[a * 3], ay = pos[a * 3 + 1], az = pos[a * 3 + 2];
    const e1x = pos[b * 3] - ax, e1y = pos[b * 3 + 1] - ay, e1z = pos[b * 3 + 2] - az;
    const e2x = pos[c * 3] - ax, e2y = pos[c * 3 + 1] - ay, e2z = pos[c * 3 + 2] - az;
    const nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
    const nl = Math.hypot(nx, ny, nz); if (nl < 1e-12) return true;
    const rx = ax - cx, ry = ay - cy, rz = az - cz, rl = Math.hypot(rx, ry, rz) || 1;
    return Math.abs(nx * rx + ny * ry + nz * rz) / (nl * rl) < minFacing;
  };
  // Where a triangle is left out (it spans a jump in depth), the near surface is carried on over it instead
  // (the bench top out to its edge): a second layer whose vertices take the nearest depth around them. The
  // shader trims it to the exact pixels the render saw at that depth. What was behind is left open: another
  // render fills it, or the gap is filled from the background around it.
  const nearDist = new Float32Array(nv);
  for (let j = 0; j <= gh; j++) {
    for (let i = 0; i <= gw; i++) {
      let m = FAR;
      for (let dj = -1; dj <= 1; dj++) {
        const jj = j + dj; if (jj < 0 || jj > gh) continue;
        for (let di = -1; di <= 1; di++) { const d = dist[at(i + di, jj)]; if (d < m) m = d; }
      }
      nearDist[j * (gw + 1) + i] = m;
    }
  }
  const all = new Float32Array(nv * 6);
  all.set(pos);
  let nAll = nv;
  const idx = new Uint32Array(gw * gh * 6);
  let ni = 0;
  const nearIndex = new Int32Array(nv).fill(-1);
  const nearVert = (k: number) => {
    if (nearIndex[k] < 0) {
      const s = nearDist[k] / dist[k];
      nearIndex[k] = nAll;
      all[nAll * 3] = cx + (pos[k * 3] - cx) * s; all[nAll * 3 + 1] = cy + (pos[k * 3 + 1] - cy) * s; all[nAll * 3 + 2] = cz + (pos[k * 3 + 2] - cz) * s;
      nAll++;
    }
    return nearIndex[k];
  };
  const back = (a: number, b: number, c: number) => {
    if (!nearLayer || Math.max(nearDist[a], nearDist[b], nearDist[c]) >= FAR) return;
    idx[ni++] = nearVert(a); idx[ni++] = nearVert(b); idx[ni++] = nearVert(c);
  };
  // Clean-up: a surface can't be where another render saw straight past it. Each neighbour's depth map
  // (min-pooled, so thin things count) is asked whether its view runs on beyond the triangle's centre; a
  // triangle that neighbours see through is a stray (a sheet stretched across a gap, a pane, a glitch).
  const views = clean > 0 && others ? others.filter((o) => Math.hypot(o[3] - cx, o[4] - cy, o[5] - cz) > 0.05) : [];
  const slack = 3.5 - 2.5 * Math.min(1, clean);
  const need = clean > 0.75 ? 1 : Math.min(2, views.length);
  const stray = (a: number, b: number, c: number) => {
    if (!views.length) return false;
    const px = (pos[a * 3] + pos[b * 3] + pos[c * 3]) / 3, py = (pos[a * 3 + 1] + pos[b * 3 + 1] + pos[c * 3 + 1]) / 3, pz = (pos[a * 3 + 2] + pos[b * 3 + 2] + pos[c * 3 + 2]) / 3;
    let hits = 0;
    for (const o of views) {
      const dx = px - o[3], dy = py - o[4], dz = pz - o[5];
      const dd = Math.hypot(dx, dy, dz);
      if (dd < 0.3) continue;
      let u = ((Math.atan2(dx, -dz) - o[6]) / (2 * Math.PI) + 0.5) % 1; if (u < 0) u += 1;
      if (flip) u = 1 - u;
      const v = Math.asin(Math.max(-1, Math.min(1, dy / dd))) / Math.PI + 0.5;
      const sx = Math.min(o[1] - 1, Math.floor(u * o[1])), sy = Math.min(o[2] - 1, Math.floor(v * o[2]));
      if (dd < o[0][sy * o[1] + sx] - (0.04 + 0.03 * dd) * slack && ++hits >= need) return true;
    }
    return false;
  };
  const gone: number[] = [];
  const drop = (a: number, b: number, c: number) => { for (const k of [a, b, c]) gone.push(pos[k * 3], pos[k * 3 + 1], pos[k * 3 + 2]); };
  let nStray = 0, nLoose = 0;
  const keep = (a: number, b: number, c: number) => {
    if (edgeOn(a, b, c)) return;
    if (stray(a, b, c)) { nStray++; drop(a, b, c); return; }
    idx[ni++] = a; idx[ni++] = b; idx[ni++] = c;
  };
  for (let j = 0; j < gh; j++) {
    for (let i = 0; i < gw; i++) {
      const a = j * (gw + 1) + i, b = a + 1, c = a + gw + 1, d = c + 1;
      // edges: a-b along the row, a-c and b-d up a column, a-d across, c-d along the next row
      const ab = jump(i, j, 1, 0), cd = jump(i, j + 1, 1, 0), ac = jump(i, j, 0, 1), bd = jump(i + 1, j, 0, 1), ad = jump(i, j, 1, 1);
      if (ab || bd || ad) back(a, b, d); else keep(a, b, d);
      if (ad || cd || ac) back(a, d, c); else keep(a, d, c);
    }
  }
  // Small loose pieces (a few triangles joined to nothing else) are noise: an anti-aliased pixel or two at
  // a different depth. Only the main surface counts; the near layer is loose on purpose.
  if (minIsland > 0) {
    const par = new Int32Array(nv);
    for (let k = 0; k < nv; k++) par[k] = k;
    const find = (k: number) => { while (par[k] !== k) { par[k] = par[par[k]]; k = par[k]; } return k; };
    // seam: the last column is the first one again
    for (let j = 0; j <= gh; j++) { const ra = find(j * (gw + 1)), rb = find(j * (gw + 1) + gw); if (ra !== rb) par[ra] = rb; }
    for (let t = 0; t < ni; t += 3) {
      const a = idx[t], b = idx[t + 1], c = idx[t + 2];
      if (a >= nv || b >= nv || c >= nv) continue;
      let ra = find(a), rb = find(b); if (ra !== rb) { par[ra] = rb; rb = find(b); }
      const rc = find(c); if (rc !== rb) par[rc] = rb;
    }
    const size = new Int32Array(nv);
    for (let t = 0; t < ni; t += 3) if (idx[t] < nv) size[find(idx[t])]++;
    let o = 0;
    for (let t = 0; t < ni; t += 3) {
      if (idx[t] < nv && size[find(idx[t])] < minIsland) { nLoose++; drop(idx[t], idx[t + 1], idx[t + 2]); continue; }
      idx[o++] = idx[t]; idx[o++] = idx[t + 1]; idx[o++] = idx[t + 2];
    }
    ni = o;
  }
  return { pos: all.slice(0, nAll * 3), idx: idx.slice(0, ni), removed: new Float32Array(gone), stray: nStray, loose: nLoose };
}

/** A depth map shrunk to `maxW` wide, keeping the nearest of each block (thin things survive), for checking against. */
export function smallDepth(data: Float32Array, w: number, h: number, maxW = 512): { data: Float32Array; w: number; h: number } {
  const s = Math.max(1, Math.round(w / maxW));
  if (s === 1) return { data, w, h };
  const sw = Math.floor(w / s), sh = Math.floor(h / s), out = new Float32Array(sw * sh);
  for (let y = 0; y < sh; y++) for (let x = 0; x < sw; x++) {
    let m = Infinity;
    for (let dy = 0; dy < s; dy++) { const row = (y * s + dy) * w + x * s; for (let dx = 0; dx < s; dx++) { const d = data[row + dx]; if (d < m) m = d; } }
    out[y * sw + x] = m;
  }
  return { data: out, w: sw, h: sh };
}

/** A neighbour's depth for depthArrays' clean-up: [data, w, h, x, y, z, yaw]. */
export type DepthOther = [Float32Array, number, number, number, number, number, number];

/** Geometry from depthArrays' result. The arrays are freed once they're on the graphics card. */
export function depthGeometryFrom(a: DepthArrays): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  const free = function (this: { array: unknown }) { this.array = null; };
  const pa = new THREE.BufferAttribute(a.pos, 3); pa.onUpload(free);
  const ia = new THREE.BufferAttribute(a.idx, 1); ia.onUpload(free);
  g.setAttribute('position', pa);
  g.setIndex(ia);
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
  return g;
}

/** Builds a depth mesh here and now (no worker). */
export function depthGeometry(depth: Float32Array, w: number, h: number, center: THREE.Vector3, yaw: number, flip: boolean, gridW = 512, minFacing = 0.12): THREE.BufferGeometry {
  return depthGeometryFrom(depthArrays(depth, w, h, center.x, center.y, center.z, yaw, flip, gridW, minFacing));
}

/**
 * Builds depth meshes off the main thread, so walking doesn't stall. Falls back to building them here
 * if a worker can't start.
 */
export class DepthBuilder {
  private worker: Worker | null = null;
  private next = 1;
  private waiting = new Map<number, (a: DepthArrays) => void>();
  constructor() {
    try {
      const src = `const build = ${depthArrays.toString()};\nself.onmessage = (e) => { const a = e.data.args; const r = build(a[0], a[1], a[2], a[3], a[4], a[5], a[6], a[7], a[8], a[9], true, a[10], a[11], a[12]); self.postMessage({ id: e.data.id, pos: r.pos, idx: r.idx, removed: r.removed, stray: r.stray, loose: r.loose }, [r.pos.buffer, r.idx.buffer, r.removed.buffer]); };`;
      this.worker = new Worker(URL.createObjectURL(new Blob([src], { type: 'text/javascript' })));
      this.worker.onmessage = (e: MessageEvent) => { const f = this.waiting.get(e.data.id); this.waiting.delete(e.data.id); f?.({ pos: e.data.pos, idx: e.data.idx, removed: e.data.removed, stray: e.data.stray, loose: e.data.loose }); };
      this.worker.onerror = () => { this.worker = null; };
    } catch { this.worker = null; }
  }
  build(depth: Float32Array, w: number, h: number, c: THREE.Vector3, yaw: number, flip: boolean, gridW: number, minFacing = 0.12, others: DepthOther[] | null = null, clean = 0, minIsland = 0): Promise<DepthArrays> {
    if (!this.worker) return Promise.resolve(depthArrays(depth, w, h, c.x, c.y, c.z, yaw, flip, gridW, minFacing, true, others, clean, minIsland));
    const id = this.next++;
    return new Promise((res) => { this.waiting.set(id, res); this.worker!.postMessage({ id, args: [depth, w, h, c.x, c.y, c.z, yaw, flip, gridW, minFacing, others, clean, minIsland] }); });
  }
}

/** Float texture of the depth, for per-pixel occlusion in the projection shader. */
export function depthTexture(depth: Float32Array, w: number, h: number): THREE.DataTexture {
  const t = new THREE.DataTexture(depth, w, h, THREE.RedFormat, THREE.FloatType);
  t.minFilter = THREE.NearestFilter; t.magFilter = THREE.NearestFilter;
  t.wrapS = THREE.RepeatWrapping; t.wrapT = THREE.ClampToEdgeWrapping;
  t.generateMipmaps = false;
  t.needsUpdate = true;
  return t;
}

export interface DepthFit {
  /** metres = a + b * raw */
  a: number;
  b: number;
  /** The file's rows run top to bottom (EXR order) and were turned over. */
  flipRows: boolean;
  /** Median difference from the proxy model, metres, over the samples that agree. */
  error: number;
  /** Share of samples within 10 cm of the proxy (walls, floors and ceilings should be). */
  agree: number;
}

/**
 * Works out how a depth file's values map to metres by comparing them with the proxy model, so the studio
 * doesn't have to say which CZDepth range, units or direction (white near or far) they rendered with.
 * `proxy(u, v)` returns the distance to the proxy in that pixel's direction (v = 0 at the bottom), or null.
 * `topFirst`: the file's rows run top to bottom (true for EXR); left out, both are tried.
 */
export function fitDepth(raw: Float32Array, w: number, h: number, proxy: (u: number, v: number) => number | null, topFirst?: boolean): DepthFit | null {
  const samples: Array<{ u: number; v: number; d: number }> = [];
  for (let j = 1; j < 24; j++) {
    const v = j / 24;
    const n = Math.max(6, Math.round(48 * Math.cos((v - 0.5) * Math.PI)));
    for (let i = 0; i < n; i++) {
      const u = (i + 0.5 * (j % 2)) / n;
      const d = proxy(u, v);
      if (d != null && d > 0.1 && d < 60) samples.push({ u, v, d });
    }
  }
  if (samples.length < 30) return null;
  let best: DepthFit | null = null;
  // EXR files store the top row first; other sources are tried both ways
  for (const flipRows of topFirst == null ? [false, true] : [topFirst]) {
    const pairs: Array<[number, number]> = [];
    for (const s of samples) {
      const px = Math.min(w - 1, Math.floor(s.u * w));
      const py = Math.min(h - 1, Math.floor((flipRows ? 1 - s.v : s.v) * h));
      const r = raw[py * w + px];
      if (Number.isFinite(r)) pairs.push([r, s.d]);
    }
    const fit = robustLine(pairs);
    if (!fit) continue;
    const res = pairs.map(([r, d]) => Math.abs(fit.a + fit.b * r - d));
    const sorted = [...res].sort((x, y) => x - y);
    const error = sorted[Math.floor(sorted.length * 0.5)];
    const agree = res.filter((e) => e < 0.1).length / res.length;
    const f: DepthFit = { a: fit.a, b: fit.b, flipRows, error, agree };
    if (!best || f.agree > best.agree + 0.02 || (Math.abs(f.agree - best.agree) <= 0.02 && f.error < best.error)) best = f;
  }
  return best;
}

/** Least-squares line through the pairs that agree with it: refit on the best 60% a few times (furniture isn't in the proxy). */
function robustLine(pairs: Array<[number, number]>): { a: number; b: number } | null {
  let use = pairs;
  let a = 0, b = 1;
  for (let it = 0; it < 6; it++) {
    if (use.length < 10) return null;
    let sx = 0, sy = 0, sxx = 0, sxy = 0;
    for (const [x, y] of use) { sx += x; sy += y; sxx += x * x; sxy += x * y; }
    const n = use.length, den = n * sxx - sx * sx;
    if (Math.abs(den) < 1e-12) return null;
    b = (n * sxy - sx * sy) / den; a = (sy - b * sx) / n;
    const scored = pairs.map((p) => ({ p, e: Math.abs(a + b * p[0] - p[1]) })).sort((x, y) => x.e - y.e);
    use = scored.slice(0, Math.max(10, Math.floor(scored.length * 0.6))).map((s) => s.p);
  }
  return { a, b };
}

/**
 * Applies a fit: metres, rows bottom-up, resized to at most maxW wide (keeping the nearest value, not an
 * average, so edges stay sharp). Values beyond the fitted range (sky, open views) become DEPTH_FAR.
 */
export function applyDepthFit(raw: Float32Array, w: number, h: number, fit: DepthFit, maxW = 2048): { data: Float32Array; w: number; h: number } {
  const ow = Math.min(w, maxW), oh = Math.max(1, Math.round(ow * h / w));
  const out = new Float32Array(ow * oh);
  // the raw value of "nothing there": the extreme the renderer clamps to, on the far side
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < raw.length; i += 7) { const r = raw[i]; if (Number.isFinite(r)) { if (r < lo) lo = r; if (r > hi) hi = r; } }
  const span = Math.max(1e-6, hi - lo);
  // only a real clamp when lots of pixels sit exactly there (the sky); otherwise it's just the furthest wall
  let farRaw = fit.b > 0 ? hi : lo, atFar = 0, seen = 0;
  for (let i = 0; i < raw.length; i += 7) { seen++; if (Math.abs(raw[i] - farRaw) < span * 1e-4) atFar++; }
  if (atFar < seen * 0.003) farRaw = NaN;
  // just short of the clamp (pixels blending the clamp with something past it) is nothing measured as well:
  // left in, they'd stand as panels exactly at the CZDepth maximum, out in the garden
  // CZDepth and V-Ray's Z depth are 0 to 1 from the minimum to the maximum distance, so the maximum is at 0
  // (white near) or 1 (white far) even where the file runs a little past it, as anti-aliasing makes it
  const normalised = lo > -0.25 && hi < 1.25 && hi - lo > 0.3;
  const clampD = normalised ? fit.a + fit.b * (fit.b < 0 ? 0 : 1) : Number.isFinite(farRaw) ? fit.a + fit.b * farRaw : NaN;
  const nearClamp = Number.isFinite(clampD) && clampD > 0.5 ? clampD - Math.max(0.3, clampD * 0.03) : Infinity;
  // smaller than the file: each pixel keeps the nearest of the pixels it covers (thin things survive)
  for (let y = 0; y < oh; y++) {
    const y0 = Math.floor(y / oh * h), y1 = Math.max(y0 + 1, Math.floor((y + 1) / oh * h));
    for (let x = 0; x < ow; x++) {
      const x0 = Math.floor(x / ow * w), x1 = Math.max(x0 + 1, Math.floor((x + 1) / ow * w));
      let m = DEPTH_FAR;
      for (let sy = y0; sy < y1; sy++) {
        const ry = fit.flipRows ? h - 1 - sy : sy;
        for (let sx = x0; sx < x1; sx++) {
          const r = raw[ry * w + sx];
          let d: number;
          if (!Number.isFinite(r) || Math.abs(r - farRaw) < span * 1e-4) d = DEPTH_FAR;
          else d = fit.a + fit.b * r;
          d = d > 0.02 && d < nearClamp ? Math.min(d, DEPTH_FAR) : DEPTH_FAR;
          if (d < m) m = d;
        }
      }
      out[y * ow + x] = m;
    }
  }
  return { data: out, w: ow, h: oh };
}

/** One render's depth file and where its camera is (metres), for fitting without a proxy. */
export interface DepthView { raw: Float32Array; w: number; h: number; pos: THREE.Vector3; yaw: number }

/**
 * Works out how depth files map to metres when there's no proxy model: renders a metre or two apart see the
 * same walls, and only the right CZDepth range makes what one saw land where the other saw it. Assumes the
 * CZDepth minimum is 0 (its default) and tries white-near and white-far. `topFirst`: rows run top to bottom (EXR).
 */
export function fitDepthByViews(views: DepthView[], flip: boolean, topFirst = true): DepthFit | null {
  if (views.length < 2) return null;
  const rawAt = (v: DepthView, dx: number, dy: number, dz: number) => {
    const len = Math.hypot(dx, dy, dz) || 1;
    let u = ((Math.atan2(dx, -dz) - v.yaw) / (2 * Math.PI) + 0.5) % 1; if (u < 0) u += 1;
    if (flip) u = 1 - u;
    const vv = Math.asin(Math.max(-1, Math.min(1, dy / len))) / Math.PI + 0.5;
    const x = Math.min(v.w - 1, Math.floor(u * v.w));
    const y = Math.min(v.h - 1, Math.floor((topFirst ? 1 - vv : vv) * v.h));
    return v.raw[y * v.w + x];
  };
  // pairs of nearby renders (they share the most)
  const pairs: Array<[DepthView, DepthView]> = [];
  for (const a of views) {
    const near = views.filter((b) => b !== a).map((b) => ({ b, d: b.pos.distanceTo(a.pos) })).filter((x) => x.d > 0.3 && x.d < 6).sort((x, y) => x.d - y.d);
    for (const x of near.slice(0, 1)) pairs.push([a, x.b]);
    if (pairs.length >= 12) break;
  }
  if (!pairs.length) return null;
  // sample directions from each first render, away from the sky and the poles
  const dirs: Array<[number, number, number]> = [];
  for (let j = 1; j < 24; j++) {
    const lat = (j / 24 - 0.5) * Math.PI * 0.9, n = Math.max(8, Math.round(48 * Math.cos(lat)));
    for (let i = 0; i < n; i++) { const lon = (i + 0.5 * (j % 2)) / n * 2 * Math.PI; dirs.push([Math.cos(lat) * Math.sin(lon), Math.sin(lat), -Math.cos(lat) * Math.cos(lon)]); }
  }
  // the raw value of "nothing there" (sky or past the range): the extreme lots of pixels share
  const range = new Map<DepthView, [number, number]>();
  for (const v of views) {
    let lo = Infinity, hi = -Infinity;
    for (let i = 0; i < v.raw.length; i += 11) { const r = v.raw[i]; if (Number.isFinite(r)) { if (r < lo) lo = r; if (r > hi) hi = r; } }
    range.set(v, [lo, hi]);
  }
  const farOf = (v: DepthView, nearWhite: boolean) => range.get(v)![nearWhite ? 0 : 1];
  const score = (M: number, nearWhite: boolean, tol = 0.03) => {
    const dist = (r: number) => (nearWhite ? M * (1 - r) : M * r);
    let hit = 0, n = 0;
    for (const [A, B] of pairs) {
      const farA = farOf(A, nearWhite), farB = farOf(B, nearWhite);
      for (const [dx, dy, dz] of dirs) {
        const ra = rawAt(A, dx, dy, dz);
        if (!Number.isFinite(ra) || Math.abs(ra - farA) < 1e-4) continue;
        const da = dist(ra);
        if (!(da > 0.05)) continue;
        const px = A.pos.x + dx * da - B.pos.x, py = A.pos.y + dy * da - B.pos.y, pz = A.pos.z + dz * da - B.pos.z;
        const rb = rawAt(B, px, py, pz);
        if (!Number.isFinite(rb) || Math.abs(rb - farB) < 1e-4) continue;
        const db = dist(rb), want = Math.hypot(px, py, pz);
        n++;
        if (Math.abs(db - want) < db * tol + 0.01) hit++;
      }
    }
    return n ? hit / n : 0;
  };
  let best = { M: 0, nearWhite: true, s: -1 };
  for (const nearWhite of [true, false]) {
    for (let k = 0; k <= 60; k++) {
      const M = 0.5 * Math.pow(400, k / 60); // 0.5 m to 200 m
      const sc = score(M, nearWhite);
      if (sc > best.s) best = { M, nearWhite, s: sc };
    }
  }
  if (best.s <= 0) return null;
  // refine around the best, with a tighter test so the score has a clear peak
  const coarse = best.M;
  best.s = -1;
  for (let k = -40; k <= 40; k++) {
    const M = coarse * Math.pow(1.15, k / 40);
    const sc = score(M, best.nearWhite, 0.008);
    if (sc > best.s) best = { ...best, M, s: sc };
  }
  return { a: best.nearWhite ? best.M : 0, b: best.nearWhite ? -best.M : best.M, flipRows: topFirst, error: 0, agree: best.s };
}

/** A depth map in metres (rows bottom first) and its render's camera. */
export interface DepthViewMetric { data: Float32Array; w: number; h: number; pos: THREE.Vector3; yaw: number }

/**
 * A proxy model made from depth maps, for projects with no model file: each render's depth as a coarse mesh
 * (surfaces seen edge-on and jumps in depth left out), cut into blocks about `cell` metres across so clicks
 * and walking only test the triangles nearby. Only what some render saw is there.
 */
export function buildDepthProxy(views: DepthViewMetric[], flip: boolean, grid = 96, cell = 1.5): THREE.Group {
  const b = new DepthProxyBuilder(flip, grid, cell);
  for (const v of views) b.add(v);
  return b.finish();
}

/** buildDepthProxy one render at a time: only a small copy of each depth map is kept, so the full one can be let go. */
export class DepthProxyBuilder {
  private chunks = new Map<string, { pos: number[]; idx: number[]; map: Map<number, number>; view: number }>();
  private n = 0;
  /** small copies of every depth map: the meshes are made at the end, each checked against its neighbours */
  private views: DepthOther[] = [];
  // clean-up here is strict: a stray triangle in the model blocks walking and shows in the 3D plan, so any
  // render within `reach` metres that sees straight through one removes it
  constructor(private flip: boolean, private grid = 96, private cell = 1.5, private clean = 0.8, private reach = 12, private maxOthers = 16) {}
  add(v: DepthViewMetric): void {
    const s = smallDepth(v.data, v.w, v.h, 1024);
    this.views.push([s.data === v.data ? v.data.slice() : s.data, s.w, s.h, v.pos.x, v.pos.y, v.pos.z, v.yaw]);
  }
  private addMesh(v: DepthOther): void {
    const vi = this.n++;
    const others = this.views.filter((o) => o !== v).filter((o) => Math.hypot(o[3] - v[3], o[4] - v[4], o[5] - v[5]) < this.reach)
      .sort((a, z) => Math.hypot(a[3] - v[3], a[4] - v[4], a[5] - v[5]) - Math.hypot(z[3] - v[3], z[4] - v[4], z[5] - v[5])).slice(0, this.maxOthers);
    const a = depthArrays(v[0], v[1], v[2], v[3], v[4], v[5], v[6], this.flip, this.grid, 0.2, false, others, this.clean, this.clean > 0 ? 4 : 0);
    this.addTriangles(a.pos, a.idx, vi);
  }
  /** Triangles into blocks about `cell` metres across, so clicks and walking only test the ones nearby. */
  private addTriangles(P: Float32Array, I: Uint32Array, vi: number): void {
    const cell = this.cell;
    for (let t = 0; t < I.length; t += 3) {
      const i0 = I[t] * 3, i1 = I[t + 1] * 3, i2 = I[t + 2] * 3;
      const cx = (P[i0] + P[i1] + P[i2]) / 3, cy = (P[i0 + 1] + P[i1 + 1] + P[i2 + 1]) / 3, cz = (P[i0 + 2] + P[i1 + 2] + P[i2 + 2]) / 3;
      const key = `${Math.floor(cx / cell)},${Math.floor(cy / cell)},${Math.floor(cz / cell)}`;
      let c = this.chunks.get(key);
      if (!c) { c = { pos: [], idx: [], map: new Map(), view: vi }; this.chunks.set(key, c); }
      if (c.view !== vi) { c.map.clear(); c.view = vi; } // vertex numbers are per render
      for (const k of [I[t], I[t + 1], I[t + 2]]) {
        let n = c.map.get(k);
        if (n === undefined) { n = c.pos.length / 3; c.map.set(k, n); c.pos.push(P[k * 3], P[k * 3 + 1], P[k * 3 + 2]); }
        c.idx.push(n);
      }
    }
  }
  /** The model as one surface fused from all the depth maps (see fusion.ts): the way projects are made now. */
  async fuse(onProgress?: (f: number) => void): Promise<THREE.Group> {
    const r = await fuseDepth(this.views, this.flip, { onProgress });
    // nothing to fuse (too few points): the older way, one mesh per render
    if (!r.idx.length) return this.finish();
    this.views = [];
    this.addTriangles(r.pos, r.idx, 0);
    return this.group();
  }
  /** One mesh per render, cleaned against its neighbours (the first way models were made from depth maps). */
  finish(): THREE.Group {
    for (const v of this.views) this.addMesh(v);
    this.views = [];
    return this.group();
  }
  private group(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'depth-proxy';
    const mat = new THREE.MeshBasicMaterial();
    for (const [key, c] of this.chunks) {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(c.pos), 3));
      g.setIndex(c.pos.length / 3 > 65535 ? new THREE.Uint32BufferAttribute(c.idx, 1) : new THREE.Uint16BufferAttribute(c.idx, 1));
      g.computeVertexNormals();
      g.computeBoundingSphere();
      const m = new THREE.Mesh(g, mat);
      m.name = `depth ${key}`;
      group.add(m);
    }
    this.chunks.clear();
    return group;
  }
}

/** The heading 3ds Max's camera transform (in an EXR) points the render's centre at, degrees (0 = -Z). */
export function headingFromCamera(m: number[]): number {
  // the camera looks down its own -Z (the third row); Max (x, y, z) is (x, z, -y) here
  const dx = -m[8], dy = -m[9];
  return (Math.atan2(dx, dy) * 180) / Math.PI;
}
